import Alert from "@mui/material/Alert"
import Chip from "@mui/material/Chip"
import MenuItem from "@mui/material/MenuItem"
import TableCell from "@mui/material/TableCell"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { format } from "date-fns"
import { useCallback, useMemo, useState } from "react"

import Column from "../../../common/components/Column"
import { adminApi } from "../axios"
import AdminHeaderTableCell, {
  AdminSortableHeaderCell,
  AdminTableHeadRow,
  adminTableBodyCellSx,
} from "../components/AdminHeaderTableCell"
import AdminLiquidToolbar from "../components/AdminLiquidToolbar"
import AdminListingTable, {
  AdminTableRow,
} from "../components/AdminListingTable"
import AdminTruncatedCell from "../components/AdminTruncatedCell"
import { useAdminPaginatedQuery } from "../hooks/useAdminPaginatedQuery"

export const DEAL_CREDIT_STATES = [
  { value: "", label: "All" },
  { value: "awaiting", label: "Awaiting client details" },
  { value: "held", label: "Waiting for a OneBase account" },
  { value: "used", label: "All boards set up" },
  { value: "ended", label: "Subscription not active" },
]

const STATE_COLORS = {
  awaiting: "warning",
  held: "info",
  used: "success",
  ended: "default",
}

function formatWhen(value) {
  if (!value) return "—"
  try {
    return format(new Date(value), "MMM d, yyyy")
  } catch {
    return "—"
  }
}

/**
 * MLS boards sold on a CRM deal (Zoho addon on the client's OneBase
 * subscription): one row per deal subscription, plus totals per client.
 */
export default function AdminMlsDealCreditsTab() {
  const [state, setState] = useState("awaiting")
  const [clients, setClients] = useState([])
  const extraParams = useMemo(() => (state ? { state } : {}), [state])
  const fetchCredits = useCallback(
    (params) =>
      adminApi.fetchMlsDealCredits(params).then((response) => {
        setClients(response.data?.clients || [])
        return response
      }),
    []
  )

  const query = useAdminPaginatedQuery(fetchCredits, {
    extraParams,
    defaultSortColumn: "created",
    defaultSortDirection: "desc",
  })
  const {
    rows,
    isLoading,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    totalResultsSize,
    searchText,
    setSearchText,
    refresh,
    loadError,
  } = query

  return (
    <Column gap={2}>
      {loadError ? (
        <Alert severity="error" onClose={() => refresh()}>
          {loadError}
        </Alert>
      ) : null}
      <AdminLiquidToolbar>
        <TextField
          size="small"
          label="Search email or Zoho subscription"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          sx={{ minWidth: 280, flex: 1, maxWidth: 480 }}
        />
        <TextField
          select
          size="small"
          label="Status"
          value={state}
          onChange={(e) => setState(e.target.value)}
          sx={{ minWidth: 240 }}
        >
          {DEAL_CREDIT_STATES.map((option) => (
            <MenuItem key={option.value || "all"} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </AdminLiquidToolbar>
      {clients.length ? (
        <Typography variant="body2" color="text.secondary">
          Per client:{" "}
          {clients
            .map(
              (client) =>
                `${client.email || "(no email)"} — ${client.granted} granted, ${
                  client.used
                } used, ${client.remaining} remaining`
            )
            .join(" · ")}
        </Typography>
      ) : null}
      <AdminListingTable
        rows={rows}
        isLoading={isLoading}
        pageIndex={pageIndex}
        setPageIndex={setPageIndex}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalResultsSize={totalResultsSize}
        columnCount={8}
        tableHead={
          <AdminTableHeadRow>
            <AdminSortableHeaderCell
              columnName="client"
              label="Client"
              query={query}
              minWidth={{ xs: 120, sm: 200 }}
            />
            <AdminHeaderTableCell minWidth={{ xs: 120, sm: 220 }}>
              Status
            </AdminHeaderTableCell>
            <AdminSortableHeaderCell
              columnName="zoho_subscription_id"
              label="Deal subscription"
              query={query}
              minWidth={{ xs: 100, sm: 160 }}
            />
            <AdminSortableHeaderCell
              columnName="quantity_granted"
              label="Granted"
              query={query}
              minWidth={{ xs: 64, sm: 80 }}
            />
            <AdminHeaderTableCell minWidth={{ xs: 64, sm: 80 }}>
              Used
            </AdminHeaderTableCell>
            <AdminHeaderTableCell minWidth={{ xs: 64, sm: 80 }}>
              Remaining
            </AdminHeaderTableCell>
            <AdminSortableHeaderCell
              columnName="nudge_sent_at"
              label="Emailed"
              query={query}
              minWidth={{ xs: 80, sm: 120 }}
            />
            <AdminSortableHeaderCell
              columnName="created"
              label="Sold"
              query={query}
              minWidth={{ xs: 80, sm: 120 }}
            />
          </AdminTableHeadRow>
        }
      >
        {rows.map((row) => (
          <AdminTableRow key={row.id} hover>
            <AdminTruncatedCell value={row.user_email || row.email || "—"} />
            <TableCell sx={adminTableBodyCellSx}>
              <Chip
                size="small"
                label={row.state_label}
                color={STATE_COLORS[row.state] || "default"}
                variant="outlined"
              />
            </TableCell>
            <AdminTruncatedCell
              value={`${row.zoho_subscription_id} (${row.zoho_status || "?"})`}
            />
            <TableCell sx={adminTableBodyCellSx}>
              {row.quantity_granted}
            </TableCell>
            <TableCell sx={adminTableBodyCellSx}>{row.used}</TableCell>
            <TableCell sx={adminTableBodyCellSx}>{row.remaining}</TableCell>
            <AdminTruncatedCell
              value={
                row.reminder_sent_at
                  ? `${formatWhen(row.nudge_sent_at)} + reminder`
                  : formatWhen(row.nudge_sent_at)
              }
            />
            <AdminTruncatedCell value={formatWhen(row.created)} />
          </AdminTableRow>
        ))}
      </AdminListingTable>
    </Column>
  )
}
